// Map the API's 422 `field` paths onto the KYB form's own shape. The API returns
// a camelCase public path — `usEin`, `businessAddress.postalCode`,
// `associations[0].individual.ssn`, `documents[0].fileDataBase64` — and each one
// has a control on this form that can render its message inline.
//
// `associations[i]` is index-aligned with `values.beneficialOwners[i]`, and
// `documents[0]` is the EIN letter; both follow from how `buildKybRequest`
// serializes the form.

import { KybApiFieldError } from 'src/queries/payments/paymentsKybError'

import { KybFormValues } from './paymentsKyb.validation'

export interface ServerAddressMessages {
  line1?: string
  line2?: string
  locality?: string
  region?: string
  postalCode?: string
  country?: string
}

export interface ServerOwnerMessages {
  firstName?: string
  middleName?: string
  lastName?: string
  dateOfBirth?: string
  email?: string
  ssn?: string
  phoneNumber?: string
  ownershipPercentage?: string
  relationshipTypes?: string
  title?: string
  address?: ServerAddressMessages
}

export interface ServerFormMessages {
  fields: Partial<Record<keyof KybFormValues, string>>
  businessAddress: ServerAddressMessages
  registeredAddress: ServerAddressMessages
  owners: ServerOwnerMessages[]
}

const ADDRESS_LEAVES = [
  'line1',
  'line2',
  'locality',
  'region',
  'postalCode',
  'country',
] as const

const OWNER_LEAVES = [
  'firstName',
  'middleName',
  'lastName',
  'dateOfBirth',
  'email',
  'ssn',
] as const

/** `a[0].b` → `['a', '0', 'b']`. */
export const serverFieldSegments = (field: string): string[] =>
  field.replace(/\[(\d+)]/g, '.$1').split('.')

export const serverFieldRoot = (field: string): string => field.split(/[.[]/)[0]

const SCALAR_FIELD_TO_FORM_FIELD: { [root: string]: keyof KybFormValues } = {
  legalBusinessName: 'legalBusinessName',
  legalStructure: 'legalStructure',
  dateFormed: 'dateFormed',
  businessDescription: 'businessDescription',
  intendedUse: 'intendedUse',
  expectedActivityVolume: 'expectedActivityVolume',
  usEin: 'usEin',
  email: 'email',
  website: 'website',
  phoneNumbers: 'phoneNumber',
  businessAddress: 'businessAddress',
  documents: 'einLetterDocument',
  // MT's own snake_case roots, for a body forwarded verbatim.
  legal_business_name: 'legalBusinessName',
  legal_structure: 'legalStructure',
  date_formed: 'dateFormed',
  business_description: 'businessDescription',
  intended_use: 'intendedUse',
  expected_activity_volume: 'expectedActivityVolume',
  us_ein: 'usEin',
  identifications: 'usEin',
  phone_numbers: 'phoneNumber',
  addresses: 'businessAddress',
}

const isAddressLeaf = (leaf: string): leaf is keyof ServerAddressMessages =>
  (ADDRESS_LEAVES as readonly string[]).includes(leaf)

type ServerOwnerLeaf = Exclude<keyof ServerOwnerMessages, 'address'>

const isOwnerLeaf = (leaf: string): leaf is ServerOwnerLeaf =>
  (OWNER_LEAVES as readonly string[]).includes(leaf)

const assignOwner = (
  owners: ServerOwnerMessages[],
  index: number,
  assign: (owner: ServerOwnerMessages) => void,
): void => {
  if (!Number.isInteger(index) || index < 0) return
  owners[index] = owners[index] ?? {}
  assign(owners[index])
}

const applyOwnerError = (
  owners: ServerOwnerMessages[],
  segments: string[],
  message: string,
): void => {
  const index = Number(segments[1])
  const [, , ...rest] = segments

  if (rest.length === 1) {
    const leaf = rest[0]
    if (
      leaf === 'ownershipPercentage' ||
      leaf === 'relationshipTypes' ||
      leaf === 'title'
    ) {
      assignOwner(owners, index, (owner) => {
        owner[leaf] = owner[leaf] ?? message
      })
    }
    return
  }
  if (rest[0] !== 'individual') return

  const [, ...individual] = rest
  if (individual.length === 1 && isOwnerLeaf(individual[0])) {
    const leaf = individual[0]
    assignOwner(owners, index, (owner) => {
      owner[leaf] = owner[leaf] ?? message
    })
    return
  }
  // `phoneNumbers.0.phoneNumber` — the form collects a single phone per person.
  if (individual[0] === 'phoneNumbers' && individual.at(-1) === 'phoneNumber') {
    assignOwner(owners, index, (owner) => {
      owner.phoneNumber = owner.phoneNumber ?? message
    })
    return
  }
  if (individual[0] === 'addresses') {
    const leaf = individual.at(-1) ?? ''
    if (!isAddressLeaf(leaf)) return
    assignOwner(owners, index, (owner) => {
      const address = owner.address ?? {}
      address[leaf] = address[leaf] ?? message
      owner.address = address
    })
  }
}

/**
 * Split one 422 into the channels the form renders from: flat step-1 fields, the
 * two address blocks, and per-owner cards. First message per target wins, so the
 * banner's order decides which one lands inline.
 */
export const mapServerMessages = (
  serverErrors: KybApiFieldError[] | null,
): ServerFormMessages => {
  const result: ServerFormMessages = {
    fields: {},
    businessAddress: {},
    registeredAddress: {},
    owners: [],
  }
  if (!serverErrors) return result

  serverErrors.forEach((err) => {
    if (!err.field) return
    const segments = serverFieldSegments(err.field)
    const root = segments[0]

    if (root === 'associations' || root === 'legal_entity_associations') {
      applyOwnerError(result.owners, segments, err.message)
      return
    }

    if (
      (root === 'businessAddress' ||
        root === 'registeredAddress' ||
        root === 'addresses') &&
      segments.length > 1
    ) {
      const leaf = segments.at(-1) ?? ''
      if (isAddressLeaf(leaf)) {
        const block =
          root === 'registeredAddress' ? 'registeredAddress' : 'businessAddress'
        result[block][leaf] = result[block][leaf] ?? err.message
        return
      }
    }

    // `documents[0]` is the EIN letter; anything after it is a supporting doc.
    if (root === 'documents' && Number(segments[1]) > 0) {
      result.fields.documents = result.fields.documents ?? err.message
      return
    }

    const formField = SCALAR_FIELD_TO_FORM_FIELD[root]
    if (formField) {
      result.fields[formField] = result.fields[formField] ?? err.message
    }
  })

  return result
}

/** Which step owns a server error, so submit failures reopen the right one. */
export const stepForServerErrors = (
  serverErrors: KybApiFieldError[] | null,
): 1 | 2 => {
  if (!serverErrors) return 1
  const hasStep1 = serverErrors.some(
    (err) =>
      err.field && SCALAR_FIELD_TO_FORM_FIELD[serverFieldRoot(err.field)],
  )
  if (hasStep1) return 1
  const hasOwner = serverErrors.some(
    (err) =>
      err.field &&
      ['associations', 'legal_entity_associations'].includes(
        serverFieldRoot(err.field),
      ),
  )
  return hasOwner ? 2 : 1
}
